import React, { useMemo, useState } from 'react';
import { LoaderCircle } from 'lucide-react';

import ImageManagerDialog from './ImageManagerDialog';
import {
  createProposalId,
  hasUsableImageUrl,
} from './ImageProposalEditor';
import {
  IMAGE_LANGUAGES,
  createImageSlot,
  normalizeImageSlots,
} from './contentLayout';


const createSlotId = () => {
  const raw =
    window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
  const token = String(raw).replace(/[^A-Za-z0-9-]/g, '').slice(0, 64);
  return `img_${token}`;
};

const createSplitSlotId = (slotId, proposalIndex, usedIds) => {
  const base =
    String(slotId || 'gallery')
      .replace(/^img_/, '')
      .replace(/[^A-Za-z0-9_-]/g, '')
      .slice(0, 48) || 'gallery';
  let attempt = 0;
  let candidate;
  do {
    const suffix = attempt ? `-${attempt}` : '';
    candidate = `img_${base}-image-${proposalIndex + 1}${suffix}`;
    attempt += 1;
  } while (usedIds.has(candidate));
  usedIds.add(candidate);
  return candidate;
};

export const expandIndependentGallerySlots = (images) => {
  const normalized = normalizeImageSlots(images);
  const usedIds = new Set(normalized.map((slot) => slot.id));

  return normalized.flatMap((slot) => {
    if (slot.placement?.type === 'inline' || slot.proposals.length <= 1) {
      return [slot];
    }

    return slot.proposals.map((proposal, proposalIndex) => ({
      ...slot,
      id:
        proposalIndex === 0
          ? slot.id
          : createSplitSlotId(slot.id, proposalIndex, usedIds),
      active_proposal_ids: Object.fromEntries(
        IMAGE_LANGUAGES.map((language) => [
          language,
          slot.active_proposal_ids[language] === proposal.id
            ? proposal.id
            : null,
        ])
      ),
      proposals: [proposal],
    }));
  });
};

const GalleryImageProposalsEditor = ({
  images = [],
  onChange,
  onUploadImage,
  canEdit = false,
  canManageStructure = canEdit,
}) => {
  const slots = useMemo(
    () =>
      canManageStructure
        ? expandIndependentGallerySlots(images)
        : normalizeImageSlots(images),
    [canManageStructure, images]
  );
  const gallerySlots = slots.filter(
    (slot) => slot.placement?.type !== 'inline'
  );
  const [uploadError, setUploadError] = useState('');
  const [uploadProgress, setUploadProgress] = useState({
    current: 0,
    total: 0,
  });
  const [editingSlotId, setEditingSlotId] = useState(null);
  const editingSlot =
    gallerySlots.find((slot) => slot.id === editingSlotId) || null;

  const emitChange = (nextSlots) => {
    if (onChange) onChange(nextSlots);
  };

  const replaceSlot = (nextSlot) =>
    emitChange(
      slots.map((slot) => (slot.id === nextSlot.id ? nextSlot : slot))
    );

  const deleteSlot = (slotId) => {
    emitChange(slots.filter((slot) => slot.id !== slotId));
    setEditingSlotId(null);
  };

  const uploadGallerySlot = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (files.length === 0 || !onUploadImage) return;
    setUploadError('');
    setUploadProgress({ current: 1, total: files.length });
    try {
      const uploadedSlots = [];
      for (const [fileIndex, file] of files.entries()) {
        setUploadProgress({ current: fileIndex + 1, total: files.length });
        const uploaded = await onUploadImage(file);
        const url =
          typeof uploaded === 'string'
            ? uploaded
            : uploaded?.url || uploaded?.data?.url;
        if (!hasUsableImageUrl(url)) throw new Error('invalid image URL');
        uploadedSlots.push(
          createImageSlot({
            id: createSlotId(),
            proposalId: createProposalId(),
            url,
            captions: { fr: '', en: '', pt: '' },
            placement: { type: 'gallery' },
          })
        );
      }
      emitChange([...slots, ...uploadedSlots]);
    } catch {
      setUploadError('Le téléversement a échoué.');
    } finally {
      setUploadProgress({ current: 0, total: 0 });
    }
  };

  const isUploading = uploadProgress.total > 0;

  return (
    <section className="gallery-image-manager">
      <div className="gallery-image-manager-list">
        {gallerySlots.map((slot, slotIndex) => {
          const galleryNumber = slotIndex + 1;
          const visibleLanguages = IMAGE_LANGUAGES.filter(
            (language) => Boolean(slot.active_proposal_ids[language])
          );
          const previewProposal =
            slot.proposals.find((proposal) =>
              IMAGE_LANGUAGES.some(
                (language) => slot.active_proposal_ids[language] === proposal.id
              )
            ) || slot.proposals[0];
          return (
            <article key={slot.id} className="gallery-image-manager-card">
              <div className="gallery-image-manager-preview">
                {previewProposal && hasUsableImageUrl(previewProposal.url) ? (
                  <img
                    src={previewProposal.url}
                    alt={`Aperçu de la galerie ${galleryNumber}`}
                  />
                ) : (
                  <span>Aucun aperçu</span>
                )}
              </div>
              <div className="gallery-image-manager-content">
                <div>
                  <h4>Image de galerie {galleryNumber}</h4>
                  <p>
                    {visibleLanguages.length > 0
                      ? `Visible en ${visibleLanguages
                          .map((language) => language.toUpperCase())
                          .join(', ')}`
                      : 'Stockée mais masquée dans toutes les langues'}
                  </p>
                </div>
                <div className="gallery-image-manager-language-status" aria-label={`Affichage de l’image de galerie ${galleryNumber}`}>
                  {IMAGE_LANGUAGES.map((language) => {
                    const proposalIndex = slot.proposals.findIndex(
                      (proposal) =>
                        proposal.id === slot.active_proposal_ids[language]
                    );
                    return (
                      <span key={language}>
                        <b>{language.toUpperCase()}</b>{' '}
                        {proposalIndex >= 0 ? 'Visible' : 'Masquée'}
                      </span>
                    );
                  })}
                </div>
                {canEdit && (
                  <button
                    type="button"
                    className="gallery-image-manager-open"
                    aria-label={`Gérer les images de galerie ${galleryNumber}`}
                    onClick={() => setEditingSlotId(slot.id)}
                  >
                    Régler cette image
                  </button>
                )}
              </div>
            </article>
          );
        })}
      </div>

      {canEdit && canManageStructure && onUploadImage && (
        <label
          className="image-manager-upload-button"
          aria-disabled={isUploading ? 'true' : undefined}
        >
          {isUploading ? (
            <span className="image-manager-upload-progress" role="status" aria-live="polite">
              <LoaderCircle className="image-manager-upload-spinner" size={17} aria-hidden="true" />
              Chargement de l’image {uploadProgress.current} sur{' '}
              {uploadProgress.total}…
            </span>
          ) : (
            'Ajouter une ou plusieurs images à la galerie'
          )}
          <input
            type="file"
            accept="image/*"
            multiple
            disabled={isUploading}
            className="sr-only"
            aria-label="Ajouter un emplacement galerie"
            onChange={uploadGallerySlot}
          />
        </label>
      )}
      {uploadError && (
        <p role="alert" className="image-manager-error">
          {uploadError}
        </p>
      )}

      {editingSlot && (
        <ImageManagerDialog
          open
          slot={editingSlot}
          canEdit={canEdit}
          canManageStructure={canManageStructure}
          onClose={() => setEditingSlotId(null)}
          onSave={(nextSlot) => {
            replaceSlot(nextSlot);
            setEditingSlotId(null);
          }}
          onDeleteSlot={
            canManageStructure
              ? () => deleteSlot(editingSlot.id)
              : undefined
          }
        />
      )}
    </section>
  );
};

export default GalleryImageProposalsEditor;
